import React from 'react';
import {
    Box, Typography, Chip, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress,
} from '@mui/material';
import {
    VolForecastStatusLabels, VolForecastStatusColors,
    MarketRegimeTypeLabels, MarketRegimeTypeColors,
} from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);

const ForecastsTab = ({ data, loading, error }) => {
    if (loading) return <TabLoading />;
    if (error) return <EmptyState message={error} />;
    if (!data) return <EmptyState />;

    const {
        vol_forecasts = [],
        ensemble_results = [],
        realized_vol = [],
        implied_vol = [],
        regime_detections = [],
        liquidity_correlations = [],
    } = data;

    return (
        <Box>
            {/* Vol Forecasts */}
            <SectionTitle>Volatility Forecasts</SectionTitle>
            {vol_forecasts.length === 0 ? <EmptyState message="No volatility forecasts available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Model Type</TableCell>
                                <TableCell>Horizon</TableCell>
                                <TableCell align="right">Forecast Value</TableCell>
                                <TableCell align="right">Confidence Lower</TableCell>
                                <TableCell align="right">Confidence Upper</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Forecast Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {vol_forecasts.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{row.model_type || '-'}</TableCell>
                                    <TableCell>{row.horizon || '-'}</TableCell>
                                    <TableCell align="right">{formatNumber(row.forecast_value)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.confidence_lower)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.confidence_upper)}</TableCell>
                                    <TableCell>
                                        {row.status ? (
                                            <Chip
                                                label={VolForecastStatusLabels[row.status] || row.status}
                                                color={VolForecastStatusColors[row.status] || 'default'}
                                                size="small"
                                            />
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>{formatDate(row.forecast_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Ensemble Results */}
            <SectionTitle>Ensemble Results</SectionTitle>
            {ensemble_results.length === 0 ? <EmptyState message="No ensemble results available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Weight Method</TableCell>
                                <TableCell align="right">Ensemble Forecast</TableCell>
                                <TableCell>AI Recommendation</TableCell>
                                <TableCell>Forecast Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ensemble_results.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{row.weight_method || '-'}</TableCell>
                                    <TableCell align="right">{formatNumber(row.ensemble_forecast)}</TableCell>
                                    <TableCell>
                                        {row.ai_recommendation
                                            ? (row.ai_recommendation.length > 120
                                                ? row.ai_recommendation.substring(0, 120) + '...'
                                                : row.ai_recommendation)
                                            : '-'}
                                    </TableCell>
                                    <TableCell>{formatDate(row.forecast_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Realized Vol */}
            <SectionTitle>Realized Volatility</SectionTitle>
            {realized_vol.length === 0 ? <EmptyState message="No realized volatility data available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Estimator Type</TableCell>
                                <TableCell align="right">Annualized Value</TableCell>
                                <TableCell align="right">Window Days</TableCell>
                                <TableCell>Calculation Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {realized_vol.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{row.estimator_type || '-'}</TableCell>
                                    <TableCell align="right">{formatPercent(row.annualized_value)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.window_days)}</TableCell>
                                    <TableCell>{formatDate(row.calculation_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Implied Vol */}
            <SectionTitle>Implied Volatility</SectionTitle>
            {implied_vol.length === 0 ? <EmptyState message="No implied volatility data available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="right">IV Value</TableCell>
                                <TableCell align="right">IV Percentile</TableCell>
                                <TableCell align="right">RV Value</TableCell>
                                <TableCell>Fetch Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {implied_vol.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell align="right">{formatPercent(row.iv_value)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.iv_percentile)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.rv_value)}</TableCell>
                                    <TableCell>{formatDate(row.fetch_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Regime Detections */}
            <SectionTitle>Regime Detections</SectionTitle>
            {regime_detections.length === 0 ? <EmptyState message="No regime detection data available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Detected Regime</TableCell>
                                <TableCell align="right">Regime Probability</TableCell>
                                <TableCell>Detection Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {regime_detections.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        {row.detected_regime ? (
                                            <Chip
                                                label={MarketRegimeTypeLabels[row.detected_regime] || row.detected_regime}
                                                color={MarketRegimeTypeColors[row.detected_regime] || 'default'}
                                                size="small"
                                            />
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell align="right">{formatPercent(row.regime_probability)}</TableCell>
                                    <TableCell>{formatDate(row.detection_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Liquidity Correlations */}
            <SectionTitle>Liquidity Correlations</SectionTitle>
            {liquidity_correlations.length === 0 ? <EmptyState message="No liquidity correlation data available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="right">Correlation Coefficient</TableCell>
                                <TableCell align="right">Window Days</TableCell>
                                <TableCell>Macro Regime</TableCell>
                                <TableCell>Calculation Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {liquidity_correlations.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell align="right">{formatNumber(row.correlation_coefficient)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.window_days)}</TableCell>
                                    <TableCell>{row.macro_regime || '-'}</TableCell>
                                    <TableCell>{formatDate(row.calculation_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default ForecastsTab;
